import { baseScoringInputConfigs, ScoringInputConfig } from "fond/cityPlanner/assessConfigurationHelper";
import { FondServiceLayerIdsList, LayerGroupIds } from "fond/layers";
import { AutoDesignConfirmModalType } from "fond/project/panels/types";
import { CommentImportance, CommentState, CommentType, ExportFormats, LayerId, ProjectType, SystemOfMeasurement, WorkflowQuality } from "fond/types";
import { CurrencyUpdateSource, SystemOfMeasurementUpdateSource } from "fond/types/analytics";
import { Currency } from "fond/types/currency";
import { MultiReportCategory } from "fond/types/multiReport";

import { boolean, event, looseObject, number, object, oneOf, string, z, ZodNumber, ZodOptional } from "../schema-util";

import { layerId, noProps, projectId } from "./shared";

const projectBaseProps = {
  projectId,
};
const projectEvent = event(projectBaseProps);
export type PropjectBaseEventProps = z.infer<typeof projectEvent>;

const assessConfigEntries = baseScoringInputConfigs.map(({ id }): [ScoringInputConfig["id"], ZodOptional<ZodNumber>] => [id, z.number().optional()]);

const assessConfigSchema = assessConfigEntries.reduce(
  (acc, [key, value]) => {
    return { ...acc, [key]: value };
  },
  {} as Record<ScoringInputConfig["id"], ZodOptional<ZodNumber>>
);

const commentLengthField = number().optional().describe("The length of the (plaintext) content of the comment in characters.");
const commentParentIdField = string().describe("The comment ID this reply belongs to");
const commentEvent = projectEvent.extend({
  type: oneOf(CommentType).describe("The type of comment"),
  importance: oneOf(CommentImportance).nullable().describe("The importance level of the comment"),
  length: commentLengthField,
});

export const project = {
  "properties-panel.tab.clicked": projectEvent.extend({
    tab: oneOf(["properties", "attachments"]).describe("The tab that was clicked"),
    projectId,
  }),
  "project.market-lens.created": noProps.describe("User created a new market lens project"),
  "project.market-lens.area-draw.finish": projectEvent
    .extend({ areaCount: number().describe("Number of areas drawn") })
    .describe("After the subareas are created in the project"),
  "Toggled layer visibility": projectEvent.extend({
    layerId,
    source: oneOf(["legend", "assess-panel"]).describe("The source of the action"),
  }),
  "legend.layer-filter.changed": projectEvent.extend({
    id: string().describe("The ID of the layer filter"),
    value: string().array().describe("The value of the layer filter"),
  }),
  "project.market-lens.plan.projects.created": projectEvent
    .extend({
      selectedAreaCount: number().describe("Number of areas selected"),
      totalAreaCount: number().describe("Total number of areas"),
      previouslyGeneratedAreaCount: number().describe("Number of areas previously generated"),
    })
    .describe("When clicking 'create projects' in the 'plan' section of a market lens project."),
  "project.market-lens.assess-scoring.cancel": noProps,
  "project.market-lens.assess-scoring.saved": projectEvent.extend({
    AssessConfiguration: object(assessConfigSchema).nullable(),
  }),
  "project.market-lens.citations.open": projectEvent,
  "project.market-lens.plan.viewed": projectEvent,
  "project.market-lens.reports.viewed": projectEvent,
  "project.market-lens.report.map.category.changed": projectEvent
    .extend({
      category: oneOf(MultiReportCategory).describe("The category selected"),
    })
    .describe("User changed the category of data shown on the report map"),
  "project.market-lens.report.map.slider.changed": projectEvent.extend({
    category: oneOf(MultiReportCategory).describe("The category selected"),
    lower: number().describe("The lower bound of the slider"),
    upper: number().describe("The upper bound of the slider"),
  }),
  "project.market-lens.reports.advanced-filter.applied": projectEvent.extend({
    source: oneOf(["input", "builder"]).describe("The source of the action"),
  }),
  "project.market-lens.reports.advanced-filter.builder.opened": noProps,
  "project.market-lens.reports.advanced-filter.builder.closed": noProps,
  "Copied project": projectEvent.extend({
    type: oneOf(ProjectType).optional().describe("The type of project"),
    parentEntityType: oneOf(["folder", "multi_project"]).optional().describe("The type of the parent entity"),
    source: oneOf(["dialog", "legacy-copy"]).describe("The source of the action"),
  }),
  "Began editing": projectEvent.extend({
    layerGroupId: string(),
  }),

  "Aborted upload": projectEvent
    .extend({
      uploadId: string(),
    })
    .describe("User aborted a polygon upload"),
  "Failed upload": projectEvent.extend({ uploadId: string() }).describe("Polygon upload failed"),
  "Began upload": projectEvent.extend({ uploadId: string().describe("Usually the project ID") }).describe("User began polygon upload"),
  "Closed upload panel": projectEvent
    .extend({ layerId: oneOf(Object.values(LayerGroupIds)) })
    .describe("User cancelled the upload panel or successfully uploaded a layer"),
  "Completed upload": projectEvent.extend({ uploadId: string() }).describe("Polygon upload completed"),
  "Opened upload panel": projectEvent.extend({ layerGroupId: oneOf(Object.values(LayerGroupIds)) }).describe(
    `
      User clicked on the 'Upload' button in the input data panel.
      Note the inconsistent naming of 'layerGroupId' vs. 'layerId' in the corresponding close event
    `
  ),
  "Upload exception": projectEvent
    .extend({ message: string() })
    .describe("CONSIDER REMOVING (error tracking): An exception occurded when uploading polygon/geo data"),
  "Upload validation error": projectEvent.extend({ message: string() }).describe("A validation error occurred when uploading polygon/geo data"),

  "Accepted confirm override": projectEvent
    .extend({
      type: oneOf(AutoDesignConfirmModalType).describe("The type of confirm modal"),
    })
    .describe("Accepted the action in the auto design confirm modal"),
  "Cancelled confirmed override": projectEvent
    .extend({
      type: oneOf(AutoDesignConfirmModalType).describe("The type of confirm modal"),
    })
    .describe("Cancelled the action in the auto design confirm modal"),
  "Began polygon select": projectEvent.extend({ layerIds: oneOf(LayerId) }),
  "Begin commenting": projectEvent.describe("User began commenting somewhere on the map. (But not general project comments)"),
  "Cancelled export": projectEvent.extend({ exportId: string() }).describe("User cancelled an in-progress export"),
  "Clicked cancel generate design": projectEvent.describe("User clicked the cancel button in the solve panel or solve control"),
  "Clicked delete blockers": projectEvent.describe("User confirmed the deletion of blockers"),
  "Clicked delete drop assignments": projectEvent.describe("User confirmed the deletion of drop assignments"),
  "Clicked delete hubs": projectEvent.describe("User confirmed the deletion of moved hubs"),
  "Clicked edit drop assignments": projectEvent.describe("Clicked on the 'Edit drop assignments' button"),
  "Clicked generate design": projectEvent
    .extend({ quality: oneOf(WorkflowQuality) })
    .describe("User clicked the generate design button in the solve control"),
  "Clicked move equipment": projectEvent.describe("Clicked on the 'Move hubs' button"),
  "Clicked reset moved equipment": projectEvent.describe("User confirmed the reset of moved hubs"),
  "Closed editing": projectEvent.describe("Editing was closed explicitly in the Draw Panel or Feature Editor, or implicitly by successfully saving."),
  "Closed project setting modal": projectEvent,
  "Confirmed layer operation": noProps.describe(
    "REDUNDANT: this event is in the same path as 'Accepted confirm override' and 'Cancelled confirm override'"
  ),
  "Copied settings from existing project": projectEvent
    .extend({
      versionId: string(),
      targetVersion: string().describe("The id of the target version"),
    })
    .describe("User copied settings from an existing project in the import modal"),
  "Created export": projectEvent
    .extend({
      accountId: string().describe("The ID of the account this project belongs to"),
      bom: boolean().optional().describe("Whether the BOM is added to the export"),
      exportId: string(),
      format: oneOf(ExportFormats),
      fiberCableSplit: boolean().optional().describe("Whether fiber cables are separated by tier"),
      hubSplit: boolean().optional().describe("Whether hubs are separated by tier"),
      spliceSplit: boolean().optional().describe("Whether splices are separated by tier"),
      spliceTables: boolean().optional().describe("Whether splice tables are added to the export"),
      comments: boolean().optional().describe("Whether comments are added to the export"),
      scheduleOfRates: boolean().optional().describe("Whether the schedule of rates is added to the export"),
      constructionPrints: boolean().optional().describe("Whether construction prints are added to the export"),
    })
    .describe("User successfully created an export"),
  "Deleted export": projectEvent.extend({ exportId: string() }).describe("User successfully deleted an export"),
  "Reran export": projectEvent.extend({ exportId: string() }).describe("User successfully re-ran an export"),
  "Deleted project": projectEvent.describe("User successfully deleted a project from either the dialog or the project list toolbar"),
  "Download export": projectEvent.extend({ exportId: string() }).describe("User clicked on the download button in the complete export table"),
  "Edited feature value": projectEvent
    .extend({
      updates: looseObject({
        AddressType: string().optional(),
        NumFibers: number().optional(),
        SubTypes: string().optional(),
        Type: string().optional(),
        CostFactor: string().optional(),
      }).describe("TODO: This event should be more strict about the properties."),
    })
    .describe("User edited a feature value in the properties panel"),
  "End commenting": projectEvent.describe("User ended commenting somewhere"),
  "project.comment.added": commentEvent.describe("A comment was added to a project"),
  "project.comment.edited": commentEvent
    .extend({
      updatedFeature: boolean().optional().describe("Has the feature for the comment been changed?"),
    })
    .describe("A comment was edited"),
  "project.comment.deleted": commentEvent.describe("A comment was deleted"),
  "project.comment.navigated": commentEvent.describe("Navigated to a comment on the map from the list"),
  "project.comment.importance.changed": commentEvent
    .extend({
      newImportance: oneOf(CommentImportance).nullable().describe("The new comment importance"),
    })
    .describe("A comment's importance was updated"),
  "project.comment.state.changed": commentEvent
    .extend({
      state: oneOf(CommentState).describe("The new state of the comment"),
    })
    .describe("A comment's state has changed"),
  "project.comment.replied": commentEvent
    .extend({
      parentId: commentParentIdField,
    })
    .describe("A comment was made in reply to another comment"),
  "project.comment.reply.edited": projectEvent
    .extend({
      length: commentLengthField,
      parentId: commentParentIdField,
    })
    .describe("A comment reply was edited"),
  "project.comment.reply.deleted": projectEvent
    .extend({
      length: commentLengthField,
      parentId: commentParentIdField,
    })
    .describe("A comment reply was deleted"),
  "project.commment.share-link.clicked": commentEvent
    .extend({
      state: oneOf(CommentState).describe("The state of the comment"),
    })
    .describe("User clicked on the 'Link to this comment' menu item"),
  "Entered project map page": projectEvent.describe("User entered the main project view"),
  "load project bom.": noProps.describe("User opened the BOM modal"),

  "Market Lens.Delete.Delete project": projectEvent.describe("User deleted a market lens project via the project list toolbar"),
  "Opened project settings modal": projectEvent.describe("User clicked on Project > Settings menu item to open the project settings modal"),
  "project.layer.features.deleted": projectEvent.extend({ source: oneOf(FondServiceLayerIdsList) }).describe("User deleted features from a layer"),
  "Renamed project": projectEvent.describe("User successfully renamed a project"),
  "Reset area selection": noProps.describe(
    "User reset the area selection in the project by clicking 'Reset' in the confirmation dialog for resetting the area."
  ),
  "Saved editing": projectEvent
    .extend({
      source: oneOf(["draw-panel", "feature-editor"]).describe("The source of the action"),
    })
    .describe("User saved the editing in the draw panel or feature editor. Not sure if the feature editor case is still used"),
  "Selected files": projectEvent
    .extend({
      layerId: oneOf(LayerId).describe("TODO: Determine if this should be restricted to LayerId or freeform"),
      filenames: string().array().describe("TO REMOVE: This could be sensive data. We should record the file types, but not names."),
    })
    .describe(
      "User selected files to upload in one of the Input Data sections. Note the inconsistent naming of 'layerId' vs. 'layerGroupId' and the applicable values in the corresponding open/close upload panel events"
    ),

  // TODO these should probably be merged into a single event with attributes
  "Solve cancelled": projectEvent
    .extend({ jobIds: object({ ID: string(), JobID: string().nullable() }).array() })
    .describe("Solve status changed to cancelled"),
  "Solve completed": projectEvent
    .extend({ jobIds: object({ ID: string(), JobID: string().nullable() }).array() })
    .describe("Solve status changed to completed"),
  "Solve error": projectEvent
    .extend({ jobIds: object({ ID: string(), JobID: string().nullable() }).array() })
    .describe("Solve status changed to error"),

  "Started import": projectEvent.extend({ versionId: string() }).describe("User clicked on the 'Upload' button in the import modal"),
  "Started refreshing mlc": noProps.describe("UNUSED: (dead code path)"),

  "Style Editor : Loaded": noProps.describe("User opened the style editor"),
  "Style Editor : duplicate style": noProps.describe("User clicked on the 'Copy style' icon"),
  "Style Editor : Style Popup": noProps.describe("User opened the style popup by clicking a feature on the map"),

  "Unable to save feature": noProps.describe(
    "Could not save the feature properties in the properties panel (either one of the values was invalid or a solve was active)"
  ),
  "Update system of measurement": projectEvent
    .extend({ systemOfMeasurement: oneOf(SystemOfMeasurement), source: oneOf(SystemOfMeasurementUpdateSource) })
    .describe("User clicked 'Update settings' in the project settings dialog"),
  "Update currency": projectEvent
    .extend({ currency: oneOf(Currency), source: oneOf(CurrencyUpdateSource) })
    .describe("User updated the project's currency, either from the project settings dialog or the scenario configuration setup"),
};
